/**
 * React Query bindings for the B1.11 BusinessDashboard.
 *
 * Cache keys:
 *   - ['dashboard', 'snapshot', branchId, period]
 *
 * Stale windows (tablet-first ops):
 *   - staleTime: 60s — dashboard is a live-ish surface, not a system-of-record
 *   - refetchOnWindowFocus: true (inherited from App-level default override)
 *
 * The smart wrapper reads `branchId` from `useCurrentBranch()` and selects the
 * period via local state; insight dismissal piggybacks on the same cache.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissDashboardInsight,
  getDashboardSnapshot,
} from './dashboard-api';
import type { DashboardPeriod, DashboardSnapshot } from './types';

export const dashboardKeys = {
  all: ['dashboard'] as const,
  snapshot: (branchId: string, period: DashboardPeriod) =>
    [...dashboardKeys.all, 'snapshot', branchId, period] as const,
};

export function useDashboardSnapshot(
  branchId: string | undefined,
  period: DashboardPeriod,
) {
  return useQuery({
    queryKey: branchId
      ? dashboardKeys.snapshot(branchId, period)
      : [...dashboardKeys.all, 'snapshot', '__pending__', period],
    queryFn: () => getDashboardSnapshot({ branchId: branchId!, period }),
    enabled: Boolean(branchId),
    staleTime: 60_000,
  });
}

export function useDismissInsight(
  branchId: string | undefined,
  period: DashboardPeriod,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string, { previous: DashboardSnapshot | undefined }>({
    mutationFn: (insightId) => dismissDashboardInsight(insightId),
    onMutate: async (insightId) => {
      if (!branchId) return { previous: undefined };
      const key = dashboardKeys.snapshot(branchId, period);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<DashboardSnapshot>(key);
      if (previous) {
        qc.setQueryData<DashboardSnapshot>(key, {
          ...previous,
          aiInsights: previous.aiInsights.filter((i) => i.id !== insightId),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (!branchId || !ctx?.previous) return;
      qc.setQueryData(
        dashboardKeys.snapshot(branchId, period),
        ctx.previous,
      );
    },
    onSettled: () => {
      if (!branchId) return;
      qc.invalidateQueries({ queryKey: dashboardKeys.snapshot(branchId, period) });
    },
  });
}
